import React, { useEffect, useRef, useState } from "react";
import {
    Circle,
    Activity,
    CheckCircle2,
    SkipForward,
    Play,
    Check,
    RotateCcw,
    MoreHorizontal,
    PartyPopper,
} from "lucide-react";
import ProgressRing from "@/components/ProgressRing";

/**
 * State-of-the-art status / progress control used on every resource card.
 *
 * The control is composed of:
 *   • Animated circular progress ring (0 / 50 / 100 / faded for skipped).
 *   • Primary action button whose label + icon adapts to current status —
 *     one click moves the user forward: Start → Mark done → Re-open.
 *   • Overflow menu (kebab) for Skip / Reset / explicit status pick.
 *   • One-shot celebration burst (CSS-only emoji confetti) when the user
 *     transitions a resource into the "completed" state for the first time
 *     in this session — instant feedback that earns the click.
 *
 * Props:
 *   status      — current status string
 *   onChange    — (next: string) => void
 *   compact     — render ring-only (no primary button), used in tight spots
 *   ringSize    — px diameter of the ring
 */
const StatusControl = ({ status = "not_started", onChange, compact = false, ringSize = 44, "data-testid": testId }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [celebrate, setCelebrate] = useState(false);
    const lastStatusRef = useRef(status);
    const menuWrap = useRef(null);

    useEffect(() => {
        if (status === "completed" && lastStatusRef.current !== "completed") {
            setCelebrate(true);
            const t = setTimeout(() => setCelebrate(false), 1100);
            return () => clearTimeout(t);
        }
        lastStatusRef.current = status;
    }, [status]);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (e) => { if (!menuWrap.current?.contains(e.target)) setMenuOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [menuOpen]);

    // Primary forward action — what one click does on the main button.
    const primary = (() => {
        switch (status) {
            case "not_started":
                return { label: "Start", icon: Play,        next: "in_progress", tone: "teal" };
            case "in_progress":
                return { label: "Mark done", icon: Check,   next: "completed",   tone: "emerald" };
            case "completed":
                return { label: "Re-open", icon: RotateCcw, next: "in_progress", tone: "neutral" };
            case "skipped":
                return { label: "Resume", icon: Play,       next: "in_progress", tone: "teal" };
            default:
                return { label: "Start", icon: Play,        next: "in_progress", tone: "teal" };
        }
    })();

    const toneCls = {
        teal:    "bg-[#2EB4AC] hover:bg-[#25908A] text-white",
        emerald: "bg-emerald-500 hover:bg-emerald-600 text-white",
        neutral: "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200",
    }[primary.tone];

    const PrimaryIcon = primary.icon;

    // Ring center: a glyph rather than %.  This reads faster on a small
    // card than a number — the user sees a state, not a measurement.
    const centerIcon = (() => {
        switch (status) {
            case "completed": return <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />;
            case "in_progress": return <Activity     className="h-4 w-4 text-[#2EB4AC]"  strokeWidth={2.5} />;
            case "skipped":   return <SkipForward  className="h-4 w-4 text-violet-500"  strokeWidth={2.5} />;
            default:          return <Circle       className="h-4 w-4 text-slate-400"   strokeWidth={2.5} />;
        }
    })();

    const handlePrimary = (e) => {
        e.stopPropagation();
        onChange?.(primary.next);
    };

    return (
        <div className="relative inline-flex items-center gap-2" data-testid={testId}>
            {/* Ring */}
            <div className="relative">
                <ProgressRing status={status} size={ringSize} stroke={4} showLabel={false}>
                    {centerIcon}
                </ProgressRing>
                {celebrate && <CelebrationBurst />}
            </div>

            {/* Primary CTA + overflow menu */}
            {!compact && (
                <div className="inline-flex items-center gap-1">
                    <button
                        type="button"
                        onClick={handlePrimary}
                        data-testid={`status-primary-${status}`}
                        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${toneCls}`}
                    >
                        <PrimaryIcon className="h-3 w-3" strokeWidth={2.5} />
                        {primary.label}
                    </button>
                    <div ref={menuWrap} className="relative">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                            data-testid="status-menu-toggle"
                            aria-label="More status options"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 bottom-full mb-1 bg-white border border-slate-200 rounded-lg shadow-lg p-1 z-30 min-w-[160px]">
                                <MenuRow icon={Circle} label="Not started" active={status === "not_started"} onClick={() => { onChange?.("not_started"); setMenuOpen(false); }} />
                                <MenuRow icon={Activity} label="In progress" active={status === "in_progress"} onClick={() => { onChange?.("in_progress"); setMenuOpen(false); }} />
                                <MenuRow icon={CheckCircle2} label="Completed" active={status === "completed"} onClick={() => { onChange?.("completed"); setMenuOpen(false); }} />
                                <div className="h-px bg-slate-100 my-1" />
                                <MenuRow icon={SkipForward} label="Skip for now" active={status === "skipped"} onClick={() => { onChange?.("skipped"); setMenuOpen(false); }} tone="violet" />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const MenuRow = ({ icon: Icon, label, active, onClick, tone = "teal" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs transition-colors hover:bg-slate-50 ${
            active
                ? tone === "violet" ? "text-violet-700 font-semibold" : "text-[#1F7F79] font-semibold"
                : "text-slate-700"
        }`}
    >
        <Icon className="h-3.5 w-3.5" /> {label}
    </button>
);

// Tiny CSS-only emoji burst — fires once on transition to "completed".
const CelebrationBurst = () => (
    <div className="pointer-events-none absolute inset-0 -m-4 flex items-center justify-center" aria-hidden="true">
        <span className="celebrate-spark celebrate-spark-1">✨</span>
        <span className="celebrate-spark celebrate-spark-2">🎉</span>
        <span className="celebrate-spark celebrate-spark-3">⭐</span>
        <span className="celebrate-spark celebrate-spark-4">✨</span>
        <span className="celebrate-spark celebrate-spark-5">🎊</span>
    </div>
);

// Export the burst so other components can chain it
export { CelebrationBurst };
export default StatusControl;
